// Collection PODs — search the warehouse for proof-of-delivery photos by
// order number. Ungated: any user can use it. Images are streamed through the
// api-server proxy (imageUrl already points at /api/pod-collections/:id/image),
// so no upstream key is exposed.

import { useState } from "react";
import { Layout } from "@/components/layout";
import { useSearchPodCollections } from "@workspace/api-client-react";
import type { PodCollection, PodCollectionList } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PackageSearch, Search, Loader2, X, ImageOff, User, Calendar } from "lucide-react";
import { format, parseISO } from "date-fns";

interface Filters {
  orderNumber?: string;
  limit?: number;
}

const DEFAULT_LIMIT = 60;

function formatCaptured(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return format(parseISO(value), "d MMM yyyy, HH:mm");
  } catch {
    return value;
  }
}

export default function CollectionPods() {
  // Draft form state vs. the applied filters that actually drive the query.
  const [orderNumber, setOrderNumber] = useState("");
  const [applied, setApplied] = useState<Filters>({ limit: DEFAULT_LIMIT });
  const [lightbox, setLightbox] = useState<PodCollection | null>(null);

  const query = useSearchPodCollections(applied, {
    query: { placeholderData: (prev: PodCollectionList | undefined) => prev } as any,
  });

  const collections = query.data?.collections ?? [];

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setApplied({
      orderNumber: orderNumber.trim() || undefined,
      limit: DEFAULT_LIMIT,
    });
  };

  const onClear = () => {
    setOrderNumber("");
    setApplied({ limit: DEFAULT_LIMIT });
  };

  const hasFilters = !!applied.orderNumber;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <PackageSearch className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-duffells-grey">Collection PODs</h1>
            <p className="text-sm text-duffells-grey/60">
              Proof-of-delivery photos captured in the warehouse against an order.
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 grid gap-4 sm:grid-cols-[1fr_auto] items-end"
        >
          <div className="space-y-1.5">
            <Label htmlFor="orderNumber">Order number</Label>
            <Input
              id="orderNumber"
              placeholder="e.g. 1234567"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              data-testid="input-order-number"
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={query.isFetching} data-testid="button-search">
              {query.isFetching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Search
            </Button>
            {hasFilters && (
              <Button type="button" variant="outline" onClick={onClear} data-testid="button-clear">
                Clear
              </Button>
            )}
          </div>
        </form>

        {query.isError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {(query.error as Error)?.message || "Failed to load collection PODs."}
          </div>
        )}

        {!query.isError && (
          <div className="flex items-center justify-between text-sm text-duffells-grey/60">
            <span>
              {query.isLoading
                ? "Loading…"
                : `${query.data?.count ?? collections.length} result${
                    (query.data?.count ?? collections.length) === 1 ? "" : "s"
                  }`}
            </span>
          </div>
        )}

        {query.isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="aspect-[4/3] bg-slate-100 animate-pulse" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3" />
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : collections.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center">
            <ImageOff className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-duffells-grey font-medium">No collection PODs found</p>
            <p className="text-sm text-duffells-grey/60 mt-1">
              {hasFilters ? "Try a different order number." : "Nothing has been captured yet."}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {collections.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setLightbox(c)}
                className="group text-left bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-primary hover:shadow-lg hover:shadow-primary/5 transition-all"
                data-testid={`card-pod-${c.id}`}
              >
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  <img
                    src={c.imageUrl}
                    alt={`POD for order ${c.orderNumber}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-3">
                  <p className="font-semibold text-duffells-grey truncate">{c.orderNumber}</p>
                  <div className="mt-1.5 space-y-1 text-xs text-duffells-grey/60">
                    <p className="flex items-center gap-1.5 truncate">
                      <User className="w-3 h-3 flex-shrink-0" />
                      {c.userName || "Unknown"}
                    </p>
                    <p className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3 flex-shrink-0" />
                      {formatCaptured(c.capturedAt)}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
        >
          <div
            className="relative max-w-4xl w-full bg-white rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="min-w-0">
                <p className="font-semibold text-duffells-grey truncate">{lightbox.orderNumber}</p>
                <p className="text-xs text-duffells-grey/60 truncate">
                  {lightbox.userName || "Unknown"} · {formatCaptured(lightbox.capturedAt)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="p-2 -mr-2 text-duffells-grey/60 hover:text-duffells-grey"
                data-testid="button-close-lightbox"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-slate-900 max-h-[75vh] overflow-auto flex items-center justify-center">
              <img
                src={lightbox.imageUrl}
                alt={`POD for order ${lightbox.orderNumber}`}
                className="max-w-full max-h-[75vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
